import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function DigitalViolence() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <img
          src="https://public.readdy.ai/ai/img_res/de4ce0c4341719f33c653de8a17fb4c6.jpg"
          alt="Digital Violence Against Women"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <span className="inline-block px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full mb-6">
            Digital Rights
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            The Invisible Bruises: Digital Violence Against Women
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            When harassment moves online, it doesn't become less real—it becomes inescapable
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <span>15 min read</span>
            <span>•</span>
            <span>January 2025</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            A man posts his opinion online. The responses critique his argument, debate his facts, challenge his logic.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            A woman posts the same opinion. The responses attack her appearance, her weight, her age, her worth as a human being.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            <strong>This is digital violence. And it's not the same for everyone.</strong>
          </p>
        </div>

        {/* The Gender Gap in Online Harassment */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Gender Gap in Online Harassment</h2>
          
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <h3 className="text-xl font-bold text-red-900 mb-4">The Statistics Are Shocking:</h3>
            <ul className="space-y-3 text-gray-800">
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold mt-1">•</span>
                <span><strong>73% of women</strong> have experienced online violence or witnessed it happening to other women</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold mt-1">•</span>
                <span><strong>Women are 27 times more likely</strong> than men to be harassed online</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold mt-1">•</span>
                <span><strong>Women journalists</strong> receive 3x more abusive comments than male colleagues</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold mt-1">•</span>
                <span><strong>41% of women</strong> have experienced severe online harassment including stalking, sexual harassment, and sustained harassment</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold mt-1">•</span>
                <span><strong>Women of color, LGBTQ+ women, and women with disabilities</strong> face even higher rates of abuse</span>
              </li>
            </ul>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            But the numbers don't tell the full story. It's not just <em>how often</em> women are harassed online—it's <strong>how differently</strong> they're harassed.
          </p>
        </section>

        {/* The Nature of the Attack */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Nature of the Attack: Content vs. Body</h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Men's Experience */}
            <div className="bg-blue-50 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-user-line text-3xl text-blue-600"></i>
                <h3 className="text-2xl font-bold text-gray-900">When Men Are Criticized Online</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>"Your argument is flawed"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>"You don't know what you're talking about"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>"That's a stupid opinion"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>"You're wrong about this topic"</span>
                </li>
              </ul>
              <p className="mt-6 text-sm text-gray-600 italic">
                Focus: The content, the argument, the opinion
              </p>
            </div>

            {/* Women's Experience */}
            <div className="bg-red-50 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-user-3-line text-3xl text-red-600"></i>
                <h3 className="text-2xl font-bold text-gray-900">When Women Are Criticized Online</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>"You're too fat/ugly to have an opinion"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>"No wonder you're single/divorced"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>"You need to get r*ped to learn your place"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>"Shut up and show us your body instead"</span>
                </li>
              </ul>
              <p className="mt-6 text-sm text-gray-600 italic">
                Focus: The body, appearance, sexual worth, right to exist
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">The Key Difference</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              When men face online criticism, it's usually about <strong>what they said</strong>. When women face online criticism, it's about <strong>who they are</strong>—their bodies, their appearance, their fundamental right to speak at all.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This isn't just harassment. <strong>It's an attempt to silence women by making their physical existence the subject of debate</strong>, rather than engaging with their ideas.
            </p>
          </div>
        </section>

        {/* Real Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Stories, Real Impact</h2>

          <div className="space-y-8">
            {/* Story 1 */}
            <div className="border-l-4 border-purple-600 pl-6 py-2">
              <p className="text-lg text-gray-700 italic mb-4">
                "I posted about climate change policy. Within an hour, I had 50+ comments. Only 3 were about climate change. The rest were about my weight, my hair, whether I was 'fuckable,' and threats to find where I live. My male colleague posted the same article. His comments were all policy debates."
              </p>
              <p className="text-sm text-gray-600">— Sarah, 32, Environmental Scientist, Luxembourg</p>
            </div>

            {/* Story 2 */}
            <div className="border-l-4 border-pink-600 pl-6 py-2">
              <p className="text-lg text-gray-700 italic mb-4">
                "I'm a tech journalist. Every article I write gets comments about my appearance in my profile photo. 'Too pretty to understand coding.' 'Probably slept her way to this job.' 'Would be hotter if she smiled more.' My male editor writes the same topics—no one comments on his looks."
              </p>
              <p className="text-sm text-gray-600">— Ayşe, 28, Tech Journalist, Turkey</p>
            </div>

            {/* Story 3 */}
            <div className="border-l-4 border-red-600 pl-6 py-2">
              <p className="text-lg text-gray-700 italic mb-4">
                "I shared my PhD research on social media. Someone found photos of me from 5 years ago when I was heavier. They created a collage comparing my 'before and after' with captions like 'No wonder she's angry' and 'Lost weight but still bitter.' My research was never discussed. Just my body."
              </p>
              <p className="text-sm text-gray-600">— Marie, 35, Sociology PhD, France</p>
            </div>

            {/* Story 4 */}
            <div className="border-l-4 border-emerald-600 pl-6 py-2">
              <p className="text-lg text-gray-700 italic mb-4">
                "I'm a plus-size fitness instructor. I posted a video about body-positive exercise. The comments weren't about my qualifications or methods—they were about how I 'shouldn't be teaching fitness' because of my size. Men with the same body type get comments about their techniques, not their weight."
              </p>
              <p className="text-sm text-gray-600">— Zeynep, 30, Fitness Instructor, Turkey</p>
            </div>
          </div>
        </section>

        {/* Types of Digital Violence */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Many Forms of Digital Violence Against Women</h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Body Shaming */}
            <div className="bg-white border-2 border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="ri-body-scan-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Body Shaming</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Attacking women's weight, appearance, age, or physical features to undermine their credibility and worth.
              </p>
              <p className="text-sm text-gray-600 italic">
                "Too fat," "too old," "too ugly," "needs plastic surgery"
              </p>
            </div>

            {/* Sexual Harassment */}
            <div className="bg-white border-2 border-pink-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <i className="ri-spam-2-line text-2xl text-pink-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Sexual Harassment</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Unwanted sexual comments, requests for nudes, explicit messages, and sexual threats.
              </p>
              <p className="text-sm text-gray-600 italic">
                "Send pics," "I'd do X to you," rape threats
              </p>
            </div>

            {/* Doxxing */}
            <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <i className="ri-map-pin-user-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Doxxing</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Publishing private information (address, phone number, workplace) to intimidate or enable real-world harm.
              </p>
              <p className="text-sm text-gray-600 italic">
                "I found where she lives," "Here's her work address"
              </p>
            </div>

            {/* Image-Based Abuse */}
            <div className="bg-white border-2 border-orange-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-image-edit-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Image-Based Abuse</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Sharing intimate images without consent, creating deepfake pornography, or photoshopping women into sexual situations.
              </p>
              <p className="text-sm text-gray-600 italic">
                Revenge porn, deepfakes, manipulated images
              </p>
            </div>

            {/* Cyberstalking */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-eye-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Cyberstalking</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Persistent monitoring, following across platforms, tracking location, and obsessive contact attempts.
              </p>
              <p className="text-sm text-gray-600 italic">
                "I see you were at X location," constant messages, fake accounts
              </p>
            </div>

            {/* Coordinated Harassment */}
            <div className="bg-white border-2 border-emerald-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <i className="ri-team-line text-2xl text-emerald-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Coordinated Harassment</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Organized groups targeting a woman with mass harassment, review bombing, or reputation destruction.
              </p>
              <p className="text-sm text-gray-600 italic">
                "Brigading," mass reporting, coordinated attacks
              </p>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Digital Violence Is Real Violence</h2>

          <div className="bg-red-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-red-900 mb-6">The Real-World Consequences</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">🧠 Mental Health Impact</h4>
                <p className="text-gray-700">
                  Women who experience online harassment report higher rates of anxiety, depression, PTSD, and suicidal thoughts. The constant barrage of abuse creates chronic stress and hypervigilance.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">💼 Professional Consequences</h4>
                <p className="text-gray-700">
                  38% of women who experience severe online harassment say it damaged their professional reputation. Many women leave their careers, delete their online presence, or avoid speaking publicly to escape abuse.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">🔇 Self-Censorship</h4>
                <p className="text-gray-700">
                  Women change what they post, how they look in photos, and what opinions they share to avoid harassment. This silencing effect means women's voices are systematically removed from public discourse.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">💰 Economic Impact</h4>
                <p className="text-gray-700">
                  Women lose income from having to leave platforms, close businesses, or abandon online work. Some spend thousands on security measures, legal fees, or therapy to cope with harassment.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">⚠️ Physical Safety Risks</h4>
                <p className="text-gray-700">
                  Digital violence often escalates to real-world violence. Doxxing leads to stalking. Online threats become physical assaults. 26% of women who experience online harassment also experience offline harassment.
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed">
            <strong>Digital violence isn't "just online."</strong> It's real violence with real consequences. The bruises may be invisible, but the trauma is profound and lasting.
          </p>
        </section>

        {/* The Intersectional Reality */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Who Faces the Worst Abuse?</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While all women face digital violence, some women face exponentially more abuse due to intersecting identities:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4 bg-purple-50 rounded-lg p-4">
              <i className="ri-user-heart-line text-2xl text-purple-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Women of Color</h4>
                <p className="text-gray-700">Face racist and sexist abuse simultaneously, with attacks on both their gender and race.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-pink-50 rounded-lg p-4">
              <i className="ri-rainbow-line text-2xl text-pink-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">LGBTQ+ Women</h4>
                <p className="text-gray-700">Experience homophobic and transphobic harassment on top of misogyny, including threats of "corrective" violence.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-blue-50 rounded-lg p-4">
              <i className="ri-wheelchair-line text-2xl text-blue-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Women with Disabilities</h4>
                <p className="text-gray-700">Face ableist abuse questioning their worth, intelligence, and right to participate in public life.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-emerald-50 rounded-lg p-4">
              <i className="ri-global-line text-2xl text-emerald-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Muslim Women</h4>
                <p className="text-gray-700">Experience Islamophobic harassment, with attacks on their faith, clothing choices, and cultural identity.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-orange-50 rounded-lg p-4">
              <i className="ri-body-scan-line text-2xl text-orange-600 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Plus-Size Women</h4>
                <p className="text-gray-700">Face relentless body-shaming and fatphobic abuse, with their weight used to dismiss any opinion they express.</p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed italic">
            The more marginalized identities a woman holds, the more severe and frequent the abuse becomes.
          </p>
        </section>

        {/* What Needs to Change */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Needs to Change</h2>

          <div className="space-y-8">
            {/* Platform Accountability */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">🏢 Platform Accountability</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  Social media companies must take responsibility for the violence happening on their platforms:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Hire more human moderators (not just AI) who understand gendered harassment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Create faster response times for reports of harassment and threats</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Implement real consequences for abusers (not just temporary bans)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Provide better tools for users to control their experience and block harassment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Publish transparency reports on harassment and what actions were taken</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal Reform */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">⚖️ Legal Reform</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  Laws must recognize digital violence as real violence:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Criminalize image-based abuse, doxxing, and cyberstalking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Create specialized cybercrime units trained in digital violence against women</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Make platforms legally liable for failing to remove illegal content</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Provide legal aid for victims pursuing cases against online abusers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Recognize digital violence as grounds for restraining orders</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Education */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">📚 Education</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  We need to teach digital citizenship and respect:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Include digital ethics and online harassment in school curricula</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Teach boys and men about consent, respect, and the impact of their words</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Train teachers, parents, and employers to recognize and address digital violence</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Create public awareness campaigns about the real harm of online abuse</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Support Services */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">💜 Support Services</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  Victims need accessible support:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Fund specialized counseling for victims of digital violence</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Create hotlines and online resources for immediate help</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Provide technical support to help victims secure their accounts and remove content</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 mt-1"></i>
                    <span>Offer financial assistance for security measures and legal fees</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* What You Can Do */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What You Can Do Right Now</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* If You're Being Harassed */}
            <div className="bg-purple-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-purple-900 mb-4">If You're Being Harassed:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Document everything</strong> - Take screenshots with dates and URLs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Report to the platform</strong> - Even if they don't act, create a record</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Block and don't engage</strong> - Responding often escalates abuse</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Secure your accounts</strong> - Use strong passwords and two-factor authentication</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Reach out for support</strong> - Talk to friends, family, or professionals</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-purple-600 mt-1"></i>
                  <span><strong>Consider legal action</strong> - Especially for threats, doxxing, or image-based abuse</span>
                </li>
              </ul>
            </div>

            {/* If You Witness Harassment */}
            <div className="bg-pink-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-pink-900 mb-4">If You Witness Harassment:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Don't stay silent</strong> - Speak up against abuse when you see it</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Support the victim</strong> - Send a kind message, report the abuse, amplify their voice</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Report the harassment</strong> - Use platform reporting tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Don't share or amplify</strong> - Don't screenshot or spread the abuse</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Educate others</strong> - Call out friends who make sexist jokes or comments</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-line text-pink-600 mt-1"></i>
                  <span><strong>Advocate for change</strong> - Demand better from platforms and lawmakers</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 md:p-12 text-white">
            <h2 className="text-3xl font-bold mb-6">The Internet Should Be Safe for Everyone</h2>
            <p className="text-xl leading-relaxed mb-6">
              Digital violence against women isn't inevitable. It's not "just how the internet is." It's a choice—a choice to allow misogyny, harassment, and abuse to flourish unchecked.
            </p>
            <p className="text-xl leading-relaxed mb-6">
              We can choose differently. We can demand platforms take responsibility. We can support victims. We can educate the next generation. We can create laws that protect women online as much as offline.
            </p>
            <p className="text-xl leading-relaxed font-bold">
              Because women deserve to exist online—to speak, to create, to work, to simply be—without fear of violence.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-gray-50 rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Take Action Now</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Link
              to="/demands"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-600 transition-colors">
                <i className="ri-file-list-3-line text-3xl text-purple-600 group-hover:text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sign the Petition</h3>
              <p className="text-gray-600">Demand legal protection against digital violence</p>
            </Link>

            <Link
              to="/safety"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                <i className="ri-shield-check-line text-3xl text-red-600 group-hover:text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Safety Tools</h3>
              <p className="text-gray-600">Access emergency contacts and protection resources</p>
            </Link>

            <Link
              to="/donate"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-pink-600 transition-colors">
                <i className="ri-hand-heart-line text-3xl text-pink-600 group-hover:text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Support Victims</h3>
              <p className="text-gray-600">Fund counseling and legal aid for survivors</p>
            </Link>
          </div>

          <div className="text-center">
            <p className="text-lg text-gray-700 mb-4">
              <strong>Share this article</strong> to raise awareness about digital violence against women
            </p>
            <div className="flex items-center justify-center gap-4">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap">
                <i className="ri-facebook-fill"></i>
                Share on Facebook
              </button>
              <button className="px-6 py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors flex items-center gap-2 whitespace-nowrap">
                <i className="ri-twitter-x-line"></i>
                Share on X
              </button>
              <button className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors flex items-center gap-2 whitespace-nowrap">
                <i className="ri-instagram-line"></i>
                Share on Instagram
              </button>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="mt-16 pt-8 border-t-2 border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Resources & Support</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-3">🌍 International Resources</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• <strong>Cyber Civil Rights Initiative:</strong> cybercivilrights.org</li>
                <li>• <strong>HeartMob:</strong> iheartmob.org</li>
                <li>• <strong>Take Back the Tech:</strong> takebackthetech.net</li>
                <li>• <strong>Coalition Against Online Violence:</strong> onlineviolenceresponsehub.org</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-3">📞 Emergency Contacts</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• <strong>Luxembourg:</strong> Police 113 | Femmes en Détresse 40 21 31</li>
                <li>• <strong>Turkey:</strong> Police 155 | Kadın Acil Yardım 183</li>
                <li>• <strong>France:</strong> Police 17 | Violences Femmes Info 3919</li>
                <li>• <strong>Germany:</strong> Police 110 | Hilfetelefon 08000 116 016</li>
              </ul>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
